import { XCircleIcon } from '@heroicons/react/outline';
import {
  isNotionClientError,
  APIErrorCode,
  ClientErrorCode,
} from '@notionhq/client';
import { QueryDatabaseResponse } from '@notionhq/client/build/src/api-endpoints';
import { Link, MetaFunction, LinksFunction, LoaderFunction } from 'remix';
import { json, useLoaderData } from 'remix';
import invariant from 'tiny-invariant';

import { Stacked, Card, Table } from '~/components';
import { Tag } from '~/components/common/Tag';
import tableStyles from '~/styles/components/table.css';
import styles from '~/styles/routes/blog/index.css';
import {
  notion,
  publishedLinksQuery,
  tagsQuery,
  colorsQuery,
} from '~/utils/notion.server';

type LoaderData = {
  links: QueryDatabaseResponse;
  recentLinks: QueryDatabaseResponse;
  tags: QueryDatabaseResponse['results'][number]['properties'] &
    {
      id: string;
      color: QueryDatabaseResponse['results'][number]['properties'];
      isActive: boolean;
    }[];
};

const converTags = (
  tags: QueryDatabaseResponse,
  colors: QueryDatabaseResponse,
  activeTags?: string[],
) =>
  tags.results?.map(({ id: tagId, properties }) => ({
    ...properties,
    id: tagId,
    color: colors.results.find(
      ({ id }) =>
        properties?.colorRelation.type === 'relation' &&
        id === properties?.colorRelation?.relation[0]?.id,
    )?.properties,
    isActive: activeTags?.indexOf(tagId) !== -1,
  }));

export const loader: LoaderFunction = async ({ request }) => {
  const searchParams = new URL(request.url).searchParams;

  let tags;
  let colors;
  let links;
  let recentLinks;

  try {
    [tags, colors, links, recentLinks] = await Promise.all([
      notion.databases.query(tagsQuery),
      notion.databases.query(colorsQuery),
      notion.databases.query(publishedLinksQuery(searchParams.getAll('tags'))),
      notion.databases.query({
        ...publishedLinksQuery(),
        page_size: 3,
      }),
    ]);
  } catch (error: unknown) {
    if (isNotionClientError(error)) {
      switch (error.code) {
        case ClientErrorCode.RequestTimeout:
          throw new Error('Request timed out');
        case APIErrorCode.ObjectNotFound:
          throw new Error('Request timed out');
        case APIErrorCode.Unauthorized:
          throw new Error('Request timed out');
        default:
          console.error(error.code);
      }
    } else {
      console.error(error);
    }
  }

  invariant(tags?.results && colors?.results, 'no tags or colors');

  return json({
    links,
    recentLinks,
    tags: converTags(tags, colors, searchParams.getAll('tags')),
  });
};

export const links: LinksFunction = () => {
  return [
    { rel: 'stylesheet', href: styles },
    { rel: 'stylesheet', href: tableStyles },
  ];
};

export const meta: MetaFunction = () => {
  return {
    title: '@GhosteTheDev | Blog articles',
    description: 'All things created by Ghoste and where to find him!',
  };
};

export default function Links() {
  const data: LoaderData = useLoaderData();

  const activeTags = data?.tags?.filter((tag) => tag.isActive);

  return (
    <Stacked>
      <div>
        <h3> Recent articles </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-4">
          {data?.recentLinks?.results?.map((recentLink) => (
            <Card {...recentLink} isExternal />
          ))}
        </div>
      </div>
      <div>
        <div className="flex justify-end space-x-2 h-8 py-1">
          {activeTags?.map((activeTag) => (
            <Tag {...activeTag} />
          ))}
          {Boolean(activeTags?.length) && (
            <Link
              className="hover:text-gray-400"
              to={{
                search: '',
              }}
            >
              <XCircleIcon className="h-6 w-6" />
            </Link>
          )}
        </div>
        <div className="overflow-hidden shadow-xl rounded-lg border border-solid border-primary">
          <Table
            data={data?.links?.results}
            handleClick={(row) => {
              invariant(
                row.properties?.link?.type === 'url' &&
                  row.properties?.link?.url,
                'recent links should have an url',
              );
              window.open(row.properties?.link?.url, '_blank');
            }}
            renderRow={(row) => {
              invariant(
                row.properties?.title?.type === 'title',
                'recent links should have a title',
              );
              invariant(
                row.properties?.tagRelation?.type === 'relation',
                'recent links should have an relation',
              );
              invariant(
                row.properties?.link?.type === 'url' &&
                  row.properties?.link?.url,
                'recent links should have an url',
              );

              const rowTags = row.properties?.tagRelation?.relation
                .map(({ id: relationId }) =>
                  data?.tags?.find(({ id }) => id === relationId),
                )
                .filter(Boolean);

              return (
                <>
                  <Table.Cell className="truncate">
                    {row.properties?.title?.title[0]?.plain_text}
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex space-x-1">
                      {rowTags.slice(0, 3).map((tag) => (
                        <Table.Tag {...tag} />
                      ))}
                    </div>
                  </Table.Cell>
                  <Table.Actions url={row.properties?.link?.url} />
                </>
              );
            }}
          />
        </div>
      </div>
    </Stacked>
  );
}
